/* eslint-disable react/button-has-type, jsx-a11y/label-has-associated-control */
/* eslint-disable no-nested-ternary, jsx-a11y/no-autofocus, react/no-unescaped-entities */
/* eslint-disable no-unused-vars, no-useless-return, curly, nonblock-statement-body-position */
/* eslint-disable no-confusing-arrow */
import React, { useState, useEffect } from 'react';
import { SUB_SCREENS, COMP_ROWS } from '../../constants';
import { CloseIcon, EditIcon } from '../CommonUI';
import { apiClient, getCompanyCode } from '../../../../config/axios.config';
import { BASE_EMPLOYEE_URL } from '../../../../store/apiConfig';

/**
 * Compensation Sub-Screen
 * Integrates with backend payment endpoints:
 * - POST /envision/api/employee/get-payments
 * - POST /envision/api/employee/update-payments
 */
function CompensationScreen({ empName, empCode, onClose, readOnly }) {
  const [rows, setRows] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [loadingComp, setLoadingComp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const normalizeDateForInput = value => {
    if (!value) return '';
    const raw = String(value).trim();
    if (!raw) return '';
    const datePart = raw.includes('T') ? raw.split('T')[0] : raw;
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(datePart)) {
      const [dd, mm, yyyy] = datePart.split('/');
      return `${yyyy}-${mm}-${dd}`;
    }
    return '';
  };

  const formatDateForDisplay = value => {
    const normalized = normalizeDateForInput(value);
    if (!normalized) return '—';
    const [yyyy, mm, dd] = normalized.split('-');
    return `${dd}/${mm}/${yyyy}`;
  };

  /** Fetch compensation rows on mount */
  useEffect(() => {
    if (!empCode) return;

    setLoadingComp(true);
    setError(null);

    const fetchPayments = async () => {
      try {
        const coCd = getCompanyCode() || 'AE01';
        const response = await apiClient.create(
          `${BASE_EMPLOYEE_URL}/get-payments`,
          {
            employeeId: empCode,
            coCd,
          },
        );

        // Handle response - could be direct array or nested in result/data
        const paymentData = response.result || response.data || response || [];
        const paymentsArray = Array.isArray(paymentData) ? paymentData : [];

        setRows(
          paymentsArray.map(r => ({
            id: r.id || r.ID || null,
            edTyp: String(r.Typ || r.typ || r.edTyp || r.EDTyp || '').trim(),
            paymentType:
              r.paymentType ||
              r.payment_type ||
              r.Payments ||
              r.payments ||
              r.edTyp ||
              r.EDTyp ||
              'Payments',
            currency: r.currency || r.Currency || 'Dirhams',
            payCode:
              r.PayCode || r.payCode || r.pay_code || r.edCd || r.EDCd || '',
            payCodeDes:
              r.PayCodeDes ||
              r.payCodeDes ||
              r.payCode ||
              r.PayCode ||
              r.edCd ||
              r.EDCd ||
              '',
            amount:
              r.AmtVal != null
                ? String(r.AmtVal)
                : r.amtVal != null
                  ? String(r.amtVal)
                  : r.amount != null
                    ? String(r.amount)
                    : '',
            effDate: normalizeDateForInput(
              r.effDate || r.eff_date || r.EffDate || '',
            ),
          })),
        );
        setLoadingComp(false);
      } catch (err) {
        console.error('Failed to load compensation:', err);
        setError('Failed to load compensation data.');
        setRows(
          COMP_ROWS.map(r => ({ ...r, id: null, amount: '', effDate: '' })),
        );
        setLoadingComp(false);
      }
    };

    fetchPayments();
  }, [empCode]);

  useEffect(() => {
    if (readOnly) setIsEdit(false);
  }, [readOnly]);

  const updateRow = (i, field, val) => {
    setRows(p => p.map((r, j) => (j === i ? { ...r, [field]: val } : r)));
  };

  const focusAmountInputAtRow = rowIndex => {
    const nextInput = document.querySelector(
      `input[data-comp-amount-row="${rowIndex}"]`,
    );
    if (nextInput) nextInput.focus();
  };

  const total = rows.reduce((s, r) => {
    const amt = parseFloat(r.amount) || 0;
    return r.paymentType === 'Deductions' ? s - amt : s + amt;
  }, 0);

  /** Save edited compensation data */
  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const rowsMissingDate = rows.filter(r => {
        const hasAmount =
          r.amount !== null &&
          r.amount !== undefined &&
          String(r.amount).trim() !== '';
        const hasEffDate =
          r.effDate !== null &&
          r.effDate !== undefined &&
          String(r.effDate).trim() !== '';
        return hasAmount && !hasEffDate;
      });

      if (rowsMissingDate.length > 0) {
        const rowLabel =
          rowsMissingDate[0].payCodeDes ||
          rowsMissingDate[0].payCode ||
          'selected row';
        setError(
          `Effective date is required when amount is entered (${rowLabel}).`,
        );
        setSaving(false);
        return;
      }

      const employeeId = String(empCode || '').trim();
      // Build payload in requested contract:
      // { employeeId, rows: [{ employeeId, edCd, edTyp, curr, amtVal, effDate }] }
      const rowsPayload = rows
        .filter(r => r.amount !== '' && r.amount != null)
        .map(r => {
          const edCdRaw = r.payCode || r.payCodeDes || '';
          const effDateRaw = r.effDate && String(r.effDate).trim();
          const amtVal = String(r.amount).trim();

          return {
            employeeId,
            edCd: String(edCdRaw).trim(),
            edTyp: String(r.edTyp || r.paymentType || '').trim(),
            curr: String(r.currency || '').trim(),
            amtVal,
            effDate: effDateRaw || null,
          };
        })
        .filter(
          p =>
            p.employeeId &&
            p.edCd &&
            p.edTyp &&
            p.curr &&
            p.amtVal !== '' &&
            !Number.isNaN(Number(p.amtVal)),
        );

      // Temporary debug log: verify outgoing payload shape in browser devtools.
      console.log('update-payments payload', { employeeId, rows: rowsPayload });

      const response = await apiClient.create(
        `${BASE_EMPLOYEE_URL}/create-payments`,
        {
          employeeId,
          rows: rowsPayload,
        },
      );

      // Check for success in various response formats
      const isSuccess =
        response.success ||
        response.status === 'SUCCESS' ||
        response.Code === 0 ||
        (typeof response === 'string' && response.includes('success'));

      if (isSuccess) {
        setSaving(false);
        setIsEdit(false);
      } else {
        throw new Error(response.message || 'Failed to save payments');
      }
    } catch (err) {
      console.error('Failed to save compensation:', err);
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  };

  /** Cancel editing — refetch original data */
  const handleCancel = async () => {
    setIsEdit(false);

    try {
      setLoadingComp(true);
      const coCd = getCompanyCode() || 'AE01';
      const response = await apiClient.create(
        `${BASE_EMPLOYEE_URL}/get-payments`,
        {
          employeeId: empCode,
          coCd,
        },
      );

      const paymentData = response.result || response.data || response || [];
      const paymentsArray = Array.isArray(paymentData) ? paymentData : [];

      setRows(
        paymentsArray.map(r => ({
          id: r.id || r.ID || null,
          edTyp: String(r.Typ || r.typ || r.edTyp || r.EDTyp || '').trim(),
          paymentType:
            r.paymentType ||
            r.payment_type ||
            r.Payments ||
            r.payments ||
            r.edTyp ||
            r.EDTyp ||
            'Payments',
          currency: r.currency || r.Currency || 'Dirhams',
          payCode:
            r.payCode || r.pay_code || r.PayCode || r.edCd || r.EDCd || '',
          amount:
            r.AmtVal != null
              ? String(r.AmtVal)
              : r.amtVal != null
                ? String(r.amtVal)
                : r.amount != null
                  ? String(r.amount)
                  : '',
          effDate: normalizeDateForInput(
            r.effDate || r.eff_date || r.EffDate || '',
          ),
        })),
      );
      setLoadingComp(false);
    } catch (err) {
      console.error('Failed to reload compensation:', err);
      setLoadingComp(false);
    }
  };

  const compensationHeaderIcon = (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
      <path
        d="M15.6 7.4H10.8a3.3 3.3 0 000 6.6h2.4a3.3 3.3 0 010 6.6H8.4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 4.8v14.4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );

  return (
    <div className="ov">
      <div
        className="sub-modal sub-modal-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="sub-modal-top comp-modal-top">
          <div className="sub-modal-title">
            <div className="sub-modal-title-row sub-modal-title-row--stacked comp-modal-title-row">
              <span className="sub-modal-icon sub-modal-icon--lg comp-modal-icon-shell">
                <span className="comp-modal-icon-inner">
                  {compensationHeaderIcon}
                </span>
              </span>
              <div className="sub-modal-title-block">
                <h2>Compensation</h2>
                {empCode && (
                  <span className="sub-modal-emp sub-modal-emp--stacked">
                    {[empName, empCode].filter(Boolean).join(' \u00b7 ')}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {!readOnly &&
              (isEdit ? (
                <>
                  <button
                    className="btn em-btn em-btn-sm comp-hdr-btn"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn em-btn em-btn-sm comp-hdr-btn-save"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </>
              ) : (
                <button
                  className="btn em-btn em-btn-sm comp-hdr-btn"
                  onClick={() => setIsEdit(true)}
                  disabled={loadingComp}
                >
                  <EditIcon size={12} /> Edit
                </button>
              ))}
            <button className="mdl-x" onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className="sub-modal-body" style={{ padding: 0 }}>
          {error && (
            <div
              style={{
                padding: '12px 24px',
                background: 'var(--red-l)',
                color: 'var(--red)',
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {error}
            </div>
          )}
          {loadingComp ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 60,
                color: 'var(--txt3)',
                fontSize: 14,
              }}
            >
              Loading compensation data...
            </div>
          ) : (
            <div className="comp-table-wrap">
              <table className="comp-table">
                <colgroup>
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '36%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '18%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Payment Type</th>
                    <th>Currency</th>
                    <th>Pay Code</th>
                    <th>Amount</th>
                    <th>Eff. Date</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr
                      key={r.id || i}
                      className={
                        r.paymentType === 'Deductions' ? 'comp-ded-row' : ''
                      }
                    >
                      <td className="comp-type">{r.paymentType}</td>
                      <td>{r.currency}</td>
                      <td className="comp-code">{r.payCodeDes}</td>
                      <td>
                        {isEdit ? (
                          <input
                            type="number"
                            className="comp-input comp-input-amt form-control external-filter-input  form-control"
                            data-comp-amount-row={i}
                            value={r.amount}
                            onChange={e =>
                              updateRow(i, 'amount', e.target.value)
                            }
                            onKeyDown={e => {
                              if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                focusAmountInputAtRow(i + 1);
                              }
                              if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                focusAmountInputAtRow(i - 1);
                              }
                            }}
                            placeholder="0"
                          />
                        ) : (
                          <div className="comp-val-amt">{r.amount || '—'}</div>
                        )}
                      </td>
                      <td>
                        {isEdit ? (
                          <div className="dw" style={{ width: '100%' }}>
                            <div className="d-ico">
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <rect
                                  x="3"
                                  y="4"
                                  width="18"
                                  height="18"
                                  rx="2"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                />
                                <path
                                  d="M16 2v4M8 2v4M3 10h18"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </div>
                            <input
                              type="text"
                              readOnly
                              placeholder="DD/MM/YYYY"
                              onClick={e => {
                                const picker = e.currentTarget.nextSibling;
                                if (picker && picker.showPicker) {
                                  picker.showPicker();
                                }
                              }}
                              className="comp-input comp-input-date form-control external-filter-input  form-control cursor-pointer"
                              value={
                                r.effDate ? formatDateForDisplay(r.effDate) : ''
                              }
                            />
                            <input
                              type="date"
                              style={{
                                position: 'absolute',
                                opacity: 0,
                                width: 0,
                                height: 0,
                                pointerEvents: 'none',
                              }}
                              tabIndex={-1}
                              aria-hidden="true"
                              value={r.effDate || ''}
                              onChange={e =>
                                updateRow(i, 'effDate', e.target.value)
                              }
                            />
                          </div>
                        ) : (
                          <div className="comp-val">
                            {formatDateForDisplay(r.effDate)}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="comp-footer">
            <span className="comp-footer-label">TOTAL PACKAGE</span>
            <span className="comp-footer-val">{total.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompensationScreen;
